import { UTMSourceEnum } from '../enums/utm-tags.enum';
import { UtmTagsDto } from '../models/dto';

export class UTMTagsBuilder {
	private utmTags: Partial<UtmTagsDto> = {};

	static create(): UTMTagsBuilder {
		return new UTMTagsBuilder();
	}

	static from(utmTags: UtmTagsDto): UTMTagsBuilder {
		const builder = new UTMTagsBuilder();
		builder.utmTags = { ...utmTags };
		return builder;
	}

	static fromQueryString(queryString: string): UtmTagsDto {
		const params = new URLSearchParams(queryString);
		const tags: Record<string, string> = {};
		
		for (const [key, value] of params.entries()) {
			if (key.toLowerCase().startsWith('utm_')) {
				const camelCaseKey = UTMTagsBuilder.convertToCamelCase(key);
				tags[camelCaseKey] = value;
			}
		}

		return tags as UtmTagsDto;
	}

	static convertToCamelCase(str: string): string {
		return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
	}

	static convertToSnakeCase(str: string): string {
		return str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
	}

	public setUtmSource(utmSource: UTMSourceEnum | string): this {
		this.utmTags.utmSource = utmSource as UTMSourceEnum;
		return this;
	}

	public setUtmMedium(utmMedium: string): this {
		this.utmTags.utmMedium = utmMedium;
		return this;
	}

	public setUtmCampaign(utmCampaign: string): this {
		this.utmTags.utmCampaign = utmCampaign;
		return this;
	}

	public setUtmContent(utmContent: string): this {
		this.utmTags.utmContent = utmContent;
		return this;
	}

	public setUtmAdset(utmAdset: string): this {
		this.utmTags.utmAdset = utmAdset;
		return this;
	}

	public setUtmAdsetId(utmAdsetId: string): this {
		this.utmTags.utmAdsetId = utmAdsetId;
		return this;
	}

	public setUtmOwner(utmOwner: string): this {
		this.utmTags.utmOwner = utmOwner;
		return this;
	}

	public setMultiple(tags: Partial<UtmTagsDto>): this {
		Object.entries(tags).forEach(([key, value]) => {
			if (value !== undefined) {
				this.utmTags[key as keyof UtmTagsDto] = value as any;
			}
		});
		return this;
	}

	public setCustom(key: string, value: string): this {
		(this.utmTags as any)[key] = value;
		return this;
	}

	public build(): UtmTagsDto {
		return { ...this.utmTags } as UtmTagsDto;
	}

	public buildQueryString(): string {
		return Object.entries(this.utmTags)
			.filter(([_, value]) => value !== undefined && value !== null && value !== '')
			.map(([key, value]) => {
				const snakeCaseKey = UTMTagsBuilder.convertToSnakeCase(key);
				return `${encodeURIComponent(snakeCaseKey)}=${encodeURIComponent(value as string)}`;
			})
			.join('&');
	}

	public inspect(): Partial<UtmTagsDto> {
		return { ...this.utmTags };
	}
}

export class UTMTagsUtils {
	static createQueryString(tags: UtmTagsDto): string {
		return UTMTagsBuilder.from(tags).buildQueryString();
	}

	static parseQueryString(queryString: string): UtmTagsDto {
		return UTMTagsBuilder.fromQueryString(queryString);
	}

	static appendQueryString(base: string, tags: UtmTagsDto): string {
		return base.includes('?') 
			? `${base}&${UTMTagsBuilder.from(tags).buildQueryString()}` 
			: `${base}?${UTMTagsBuilder.from(tags).buildQueryString()}`;
	}

	static merge(base: UtmTagsDto, override: Partial<UtmTagsDto>): UtmTagsDto {
		return UTMTagsBuilder.from(base).setMultiple(override).build();
	}

	static isEmpty(tags: UtmTagsDto): boolean {
		return Object.values(tags).every(value => !value);
	}

	static extractUtmParams(obj: Record<string, any>): Record<string, string> {
		return Object.entries(obj)
			.filter(([key]) => key.toLowerCase().startsWith('utm'))
			.reduce((acc, [key, value]) => {
				if (value !== undefined && value !== null && value !== '') {
					const normalizedKey = key.includes('_') 
						? UTMTagsBuilder.convertToCamelCase(key)
						: key;
					acc[normalizedKey] = String(value);
				}
				return acc;
			}, {} as Record<string, string>);
	}
}
